import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Main from '../../../../BrowserSupport/Main';
import { currentUserStore } from '../../../../CurrentUserStore';
import { findLink } from '../../../../LinkyLinky';
import I18nTitle from '../../../../Title/I18nTitle';
import { T, translation, TranslationsContext } from '../../../../Translations';
import GroupInputWithSuggestions from '../../../Form/GroupInputWithSuggestions';
import { GroupSuggestion, UserGroup } from '../../../Form/GroupInputWithSuggestions/Types';
import { TypeaheadSelection } from '../../../Form/TypeaheadInput/Types';
import SectionCard from '../../../People/Groups/Shared/SectionCard';
import MainNav from '../../../Platform/MainNav';
import InvitationsLink from '../SharedComponents/InvitationsLink';
import CourseField, { isCourseInvite } from './CourseField';
import Footer from './Footer';
import Header from './Header';
import { inviteTarget, InviteTarget } from './InviteTarget';
import { licenseAlertsStore } from './LicenseAlerts/LicenseAlertsStore';
import { LicenseAlertsState, Request } from './LicenseAlerts/LicenseAlertsStore/Types';
import { participantsStartingLater } from './LicenseAlerts/Selectors';
import LicenseAlertBanner from './LicenseAlertBanner';
import VipLicenseBanner from './LicenseBanner';
import Overlays from './Overlays';
import ParticipantTextArea from './ParticipantTextArea';
import SchedulingFields from './SchedulingFields';
import ShareSkeleton, { stillLoading } from './ShareSkeleton';
import { shareStore } from './Stores/ShareStore';
import { ReviewEntry, ShareBy, ShareState, ShareType } from './Stores/ShareStore/Types';

// How long the participant list has to settle before the banner asks the server
// what those people can see. Pasting a block of emails resolves them one at a
// time, and without this every one of them would be its own request.
const LICENSE_ALERTS_DEBOUNCE_MS = 400;

// The alert only has something to say while the admin is still assembling the
// list; once the invite is in flight the modal owns the screen.
const isBuildingParticipantList = (state: ShareState): boolean => {
  switch (state.kind) {
    case 'ready':
      return true;
    case 'waiting':
    case 'loading':
    case 'searching':
    case 'reviewing':
    case 'refreshing':
    case 'publishing':
    case 'submitting':
    case 'confirming':
    case 'error':
      return false;
    default:
      return assertNever(state);
  }
};

// Feature flag: "Add Participants to Group" section
// This feature was fully developed and is functional, but is being withheld from users
// for the time being per product requirements. The backend gracefully handles the absence
// of user_group data when this feature is disabled.
// Set to true to re-enable the group assignment UI.
const SHOW_ADD_TO_GROUP_SECTION = false;

const participantsDescription = (target: InviteTarget) => {
  switch (target.kind) {
    case 'collection':
      return <T kind="Enter or paste names, emails or group names to invite to this collection." />;
    case 'course':
      return <T kind="Enter or paste names, emails or group names to invite to this course." />;
    default:
      return assertNever(target);
  }
};

const scheduleDescription = (target: InviteTarget) => {
  switch (target.kind) {
    case 'collection':
      return (
        <T kind="Set the date and time to email participants their invitation to this collection." />
      );
    case 'course':
      return (
        <T kind="Set the date and time to email participants their invitation to this course." />
      );
    default:
      return assertNever(target);
  }
};

// DVLP-8068: whether anyone on the list only gains access once a later license
// starts, so the scheduling section can say what that means for them.
const anyoneStartsLater = (state: LicenseAlertsState): boolean => {
  switch (state.kind) {
    case 'ready':
      return participantsStartingLater(state.licenseAlertsResource.payload) > 0;
    case 'waiting':
    case 'loading':
    case 'error':
      return false;
    default:
      return assertNever(state);
  }
};

// DVLP-7740: the page follows the Add Participant / Add Group layout: header
// strip with the actions, numbered section cards on a muted background, and a
// footer repeating the actions.
const Share: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const ts = React.useContext(TranslationsContext);
  const target = inviteTarget(shareStore.state);

  const [groupTypeaheadInputValue, setGroupTypeaheadInputValue] = useState('');
  const groupTypeaheadEndpoint = findLink(
    'v2-participant-group-suggestions',
    currentUserStore.links,
  );

  useEffect(() => {
    const collectionId = parseInt(id!, 10);
    const shareBy = (searchParams.get('shareBy') as ShareBy) || 'email';
    const shareType = (searchParams.get('shareType') as ShareType) || 'collection';
    const programId = fromNullable(searchParams.get('programId')).map((id) => parseInt(id, 10));
    shareStore.load(collectionId, shareBy, shareType, programId);
  }, [id, searchParams]);

  // Clear local group typeahead input when store resets userGroup to unselected
  useEffect(() => {
    if (shareStore.state.kind === 'ready' && shareStore.state.userGroup.kind === 'unselected') {
      setGroupTypeaheadInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- MobX observer re-renders but useEffect needs this dep to clear input on store reset
  }, [shareStore.state]);

  const handleGroupSelectionChange = (selection: TypeaheadSelection<GroupSuggestion>) => {
    switch (selection.kind) {
      case 'existing':
        const selectedGroup: UserGroup = { id: selection.item.id, name: selection.item.name };
        shareStore.updateUserGroup({ kind: 'selected', group: selectedGroup });
        setGroupTypeaheadInputValue(selectedGroup.name);
        break;
      case 'new':
        shareStore.updateUserGroup({ kind: 'new', name: selection.name });
        setGroupTypeaheadInputValue(selection.name);
        break;
      default:
        assertNever(selection);
    }
  };

  const handleGroupInputChange = (value: string) => {
    setGroupTypeaheadInputValue(value);
    if (value === '') {
      shareStore.updateUserGroup({ kind: 'unselected' });
    } else {
      // Treat any typed text as a potential new group name
      shareStore.updateUserGroup({ kind: 'new', name: value });
    }
  };

  const licenseAlertsRequest = (learningCollectionId: number): Request => ({
    learningCollectionId,
    userIds: [...shareStore.verifiedUserIds, ...shareStore.unlicensedUserIds],
    groupIds: shareStore.verifiedGroupIds,
    notFoundEmails: shareStore.notFoundEmails,
  });

  // Serialized so the effect re-runs on a changed list rather than on every
  // observable read that hands back a fresh array.
  const participantsKey = [
    shareStore.verifiedUserIds.join(','),
    shareStore.unlicensedUserIds.join(','),
    shareStore.verifiedGroupIds.join(','),
    shareStore.notFoundEmails.join(','),
  ].join('|');

  useEffect(() => {
    if (!isBuildingParticipantList(shareStore.state)) {
      return;
    }

    const request = licenseAlertsRequest(parseInt(id!, 10));

    if (
      request.userIds.length === 0 &&
      request.groupIds.length === 0 &&
      request.notFoundEmails.length === 0
    ) {
      licenseAlertsStore.reset();
      return;
    }

    if (licenseAlertsStore.covers(request)) {
      return;
    }

    const timer = setTimeout(() => licenseAlertsStore.load(request), LICENSE_ALERTS_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [id, participantsKey]);

  // Committed group value from store (selected or new group that user has committed to)
  const selectedGroupValue = () => {
    switch (shareStore.state.kind) {
      case 'waiting':
      case 'loading':
        return '';
      case 'ready':
      case 'publishing':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'submitting':
      case 'confirming':
        switch (shareStore.state.userGroup.kind) {
          case 'selected':
            return shareStore.state.userGroup.group.name;
          case 'new':
            return shareStore.state.userGroup.name;
          case 'unselected':
            return '';
        }
      case 'error':
        return '';
    }
  };

  // Display local typeahead input if user is typing, otherwise show committed value from store
  const groupTypeaheadDisplayValue = groupTypeaheadInputValue || selectedGroupValue();

  // Course or collection is settled in the Share overlay. A course invite
  // picks its course here, as the first step; a collection invite has none.
  const courseInvite = isCourseInvite(shareStore.state);
  const sectionNumber = (position: number) => (courseInvite ? position + 1 : position);

  const openReview = (reviewEntry: ReviewEntry) => {
    switch (shareStore.state.kind) {
      case 'ready': {
        const request = licenseAlertsRequest(
          shareStore.state.learningCollectionResource.payload.id,
        );
        // The banner has usually loaded this already; only ask again if the
        // list moved since.
        if (!licenseAlertsStore.covers(request)) {
          licenseAlertsStore.load(request);
        }
        shareStore.startReview(reviewEntry);
        break;
      }
      case 'waiting':
      case 'loading':
      case 'publishing':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(shareStore.state);
    }
  };

  return (
    <>
      <I18nTitle title="Learning Collections" />
      <div className="min-h-screen flex flex-col">
        <MainNav />
        <Header onConfirm={() => openReview('confirm')} />
        <VipLicenseBanner />
        <div className="flex-grow bg-muted/30" data-intercom-target="share-invite-content">
          <div className="max-w-5xl mx-auto px-12 py-8">
            <Main id="main">
              <ShareSkeleton loading={stillLoading(shareStore.state)} />
              <div className={clsx('space-y-6', { hidden: stillLoading(shareStore.state) })}>
                <LicenseAlertBanner onViewDetails={() => openReview('license-alert')} />

                {courseInvite && (
                  <SectionCard
                    number={1}
                    title={<T kind="Choose a Course" />}
                    description={<T kind="Pick the course to invite participants to." />}
                    intercomTarget="share-course-section"
                  >
                    <CourseField />
                  </SectionCard>
                )}

                <SectionCard
                  number={sectionNumber(1)}
                  title={<T kind="Confirm Participants" />}
                  description={target.map(participantsDescription).getOrElseValue(<></>)}
                  intercomTarget="share-participants-section"
                >
                  <div className="grid gap-4" style={{ gridTemplateColumns: '1fr auto' }}>
                    <div className="min-w-0">
                      <ParticipantTextArea />
                    </div>
                    <button
                      type="button"
                      onClick={() => shareStore.startSearch()}
                      className="px-4 py-2 h-fit flex-shrink-0 border border-primary text-primary rounded-md bg-card hover:bg-primary/5 font-medium whitespace-nowrap text-sm"
                      data-intercom-target="share-select-participants-button"
                    >
                      <T kind="Select" />
                    </button>
                  </div>

                  {shareStore.totalParticipantCount > 0 && (
                    <p
                      className="mt-2 text-primary text-sm font-medium"
                      data-intercom-target={`participants-to-invite-count-${shareStore.totalParticipantCount}`}
                    >
                      <T
                        kind="Participants to invite ({{count}})"
                        count={shareStore.totalParticipantCount}
                      />
                    </p>
                  )}

                  {SHOW_ADD_TO_GROUP_SECTION && (
                    <div className="mt-6">
                      <label
                        className="block mb-2"
                        style={{ fontWeight: 'var(--font-weight-medium)' }}
                      >
                        <T kind="Add Participants to Group" />
                      </label>
                      <p
                        className="text-muted-foreground mb-2"
                        style={{ fontSize: 'var(--text-sm)' }}
                      >
                        <T kind="Group the participant to find and report on later" />
                      </p>
                      <GroupInputWithSuggestions
                        value={groupTypeaheadDisplayValue}
                        onChange={handleGroupInputChange}
                        onSelectionChange={handleGroupSelectionChange}
                        onBlur={() => {}}
                        placeholder={translation('Search groups or type new group name...')(ts)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        endpoint={groupTypeaheadEndpoint}
                        allowCreate={true}
                      />
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  number={sectionNumber(2)}
                  title={<T kind="Send a Message" />}
                  description={
                    <T kind="Optionally email participants an invitation at a scheduled time. They get access either way." />
                  }
                  intercomTarget="share-schedule-section"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-8">
                      <div
                        className="mb-1"
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        <T kind="Set an Invitation Time" />
                      </div>
                      <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                        {target.map(scheduleDescription).getOrElseValue(<></>)}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={shareStore.scheduleEnabled}
                      aria-label={translation('Toggle schedule invitation')(ts)}
                      onClick={() => shareStore.toggleScheduleEnabled()}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        shareStore.scheduleEnabled ? 'bg-primary' : 'bg-muted'
                      }`}
                      data-intercom-target="share-schedule-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                          shareStore.scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {anyoneStartsLater(licenseAlertsStore.state) && (
                    <p
                      className="mt-3 text-muted-foreground"
                      style={{ fontSize: 'var(--text-sm)' }}
                      data-intercom-target="share-schedule-license-start-note"
                    >
                      {shareStore.willSendEmail ? (
                        <T kind="Participants whose license starts later receive their invitation on their start date." />
                      ) : (
                        <T kind="Participants whose license starts later get access on their start date." />
                      )}
                    </p>
                  )}

                  {shareStore.scheduleEnabled && (
                    <div
                      className="mt-5 [&_input]:border-border [&_input]:rounded-md [&_input]:bg-background [&_select]:border-border [&_select]:rounded-md [&_select]:bg-background [&_label]:font-medium [&_label]:mb-1"
                      data-intercom-target="share-schedule-fields"
                    >
                      <SchedulingFields shareStore={shareStore} />
                    </div>
                  )}

                  <div
                    className="mt-5 pt-4 border-t border-border"
                    data-intercom-target="share-view-invitations"
                  >
                    <InvitationsLink />
                  </div>
                </SectionCard>
              </div>
            </Main>
          </div>
        </div>
        <Footer onConfirm={() => openReview('confirm')} />
      </div>
      <Overlays />
    </>
  );
};

export default observer(Share);
